import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  Clock, 
  MessageCircle, 
  Eye,
  Lightbulb,
  Shield,
  Users,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const ReasonCard = ({ icon: Icon, title, description, highlight }) => (
  <div className="bg-slate-50 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-slate-100">
    <div className="w-12 h-12 bg-amber-700/10 rounded-xl flex items-center justify-center mb-6">
      <Icon className="w-6 h-6 text-amber-700" />
    </div>
    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-slate-900 mb-3">{title}</h3>
    <p className="text-slate-600 leading-relaxed mb-4">{description}</p>
    <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
      <CheckCircle className="w-4 h-4" />
      {highlight}
    </div>
  </div>
);

const ProcessStep = ({ step, title, desc }) => (
  <div className="text-center">
    <div className="text-5xl font-bold text-amber-500/30 mb-4">{step}</div>
    <h3 className="font-semibold text-lg mb-2">{title}</h3>
    <p className="text-slate-400 text-sm">{desc}</p>
  </div>
);

const TestimonialCard = ({ quote, author, role }) => (
  <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
    <div className="mb-6">
      <svg className="w-8 h-8 text-amber-700/30" fill="currentColor" viewBox="0 0 32 32">
        <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
      </svg>
    </div>
    <p className="text-slate-600 leading-relaxed mb-6 italic">"{quote}"</p>
    <div>
      <p className="font-semibold text-slate-900">{author}</p>
      <p className="text-sm text-slate-500">{role}</p>
    </div>
  </div>
);

export const WhyPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">Why Choose Me</p>
          <h1 
            className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-slate-900 mb-6 max-w-3xl mx-auto"
            data-testid="why-title"
          >
            Because Your Closing <span className="text-amber-700">Matters</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            I'm not just processing paperwork. I'm invested in making your transaction 
            smooth, keeping you informed, and solving problems before they become crises.
          </p>
        </div>
      </section>

      {/* Reasons Grid */}
      <section className="py-16 md:py-24 bg-white" data-testid="reasons-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ReasonCard
              icon={Phone}
              title="I Actually Answer"
              description="When you call, you get me – not a voicemail maze. I believe in real conversations and quick responses."
              highlight="Response within hours, not days"
            />
            <ReasonCard
              icon={MessageCircle}
              title="Plain English Communication"
              description="Title insurance doesn't have to be confusing. I explain things clearly, without the jargon."
              highlight="No confusing legal-speak"
            />
            <ReasonCard
              icon={Eye}
              title="Proactive Problem Spotting"
              description="I don't wait for issues to become deal-breakers. By reviewing files carefully from the start, I catch potential problems early."
              highlight="Issues addressed before closing"
            />
            <ReasonCard
              icon={Lightbulb}
              title="Local Market Knowledge"
              description="Being a Philadelphia native means I understand the local market, neighborhoods, and common title issues in our area."
              highlight="35+ years in Philly area real estate"
            />
            <ReasonCard
              icon={Shield}
              title={<>Backed by <a href="https://www.alegaltitle.com" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">American Legal Abstract</a></>}
              description={<>As VP at <a href="https://www.alegaltitle.com" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">American Legal Abstract</a>, I have the full resources of a respected title company behind me.</>}
              highlight="Full-service title company"
            />
            <ReasonCard
              icon={Users}
              title="Works with Non-Directing Agents"
              description="I work seamlessly with agents who don't direct title business, providing the same level of service and attention."
              highlight="Equal treatment for all clients"
            />
          </div>
        </div>
      </section>

      {/* How I Work Section */}
      <section className="py-16 md:py-24 bg-slate-900 text-white" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-amber-500 font-medium tracking-wide uppercase text-sm mb-4">The Process</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold mb-6">
              How I Work With You
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <ProcessStep step="01" title="Initial Contact" desc="Reach out by phone, email, or form. I'll respond quickly to discuss your needs." />
            <ProcessStep step="02" title="Title Order" desc="Once you're ready, I open your file and begin the title search process." />
            <ProcessStep step="03" title="Review & Communication" desc="I review findings, address any issues, and keep you updated at every step." />
            <ProcessStep step="04" title="Smooth Closing" desc="We coordinate the closing, handle the details, and celebrate your success." />
          </div>
        </div>
      </section>

      {/* Testimonials Placeholder */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">Testimonials</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              What Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <TestimonialCard
              quote="American Legal has always been my go to for title. They provide great customer service and great support throughout the entire process. Terry does the best title!"
              author="Michael S."
              role="5-Star Review"
            />
            <TestimonialCard
              quote="Terry and team made the process smooth and enjoyable. Buying a home is exciting but also scary.. They took out the scary and added to the exciting with cupcakes to celebrate. Highly recommend!"
              author="Diane"
              role="5-Star Review"
            />
            <TestimonialCard
              quote="Terry was great - checked in with me weekly to update me on title. Thank You!"
              author="Antonella D."
              role="5-Star Review"
            />
            <TestimonialCard
              quote="Was an absolute pleasure working with Terry Katz and American Legal Abstract. Terry walked me through the entire process from Day 1, and I felt like I was in great hands along the way. They were on my side and helping me when any issues arose in the sale, and made me feel comfortable until I signed the closing papers, and even for the few weeks following closing. I would 100% work with them again for my next home purchase or sale."
              author="Mike P."
              role="5-Star Review"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Let's talk about your upcoming transaction. No pressure, just a conversation 
            about how I can help make your closing smooth and stress-free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="why-cta-primary">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-4 font-medium transition-all hover:scale-105">
                <Phone className="w-5 h-5 mr-2" />
                Let's Talk Title
              </Button>
            </Link>
            <Link to="/services" data-testid="why-cta-secondary">
              <Button 
                variant="outline" 
                className="border-2 border-slate-200 hover:border-slate-900 rounded-full px-8 py-4 font-medium"
              >
                View Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
